import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  type OnInit,
  type Signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { Status, type AsyncState } from '@essent/common';
import { TrackFieldfocusDirective } from '@analytics/events';
import {
  CrntFormControl,
  CrntFormControlHelpText,
  CrntFormControlLabel,
  CrntIcon,
  CrntTextInput,
} from '@essent/current-web-angular';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { InputErrorComponent } from '@innogy/common-ui/forms';

import { RfaFormControl } from '../../../level1';
import { focusChangeOptionsToConfig } from '../../helpers/tracker-options-to-config';
import type { RfaAddress, RfaAddressControls } from '../../models';
import type { RfaFocusChangeTrackerOptions } from '../../models/rfa-tracker-options.interface';
import type { RfaComponents } from '../RfaComponents';
import { getAriaDescribedBy } from '../../helpers/aria-fn-helpers';
import { RfaErrorsComponent } from '../utils/rfa-errors/rfa-errors.component';

@Component({
  selector: 'rfa-address',
  standalone: true,
  templateUrl: './rfa-address.component.html',
  styleUrls: [
    '../styles.essent.scss',
    '../styles.ed.scss',
    './rfa-address.component.scss',
  ],
  imports: [
    ReactiveFormsModule,
    RfaErrorsComponent,
    CrntFormControl,
    CrntFormControlLabel,
    CrntFormControlHelpText,
    CrntTextInput,
    TrackFieldfocusDirective,
    AutomationIdDirective,
    CrntIcon,
    InputErrorComponent,
  ],
})
export class RfaAddressComponent implements OnInit {
  public readonly form = input.required<FormGroup<RfaAddressControls>>();
  public readonly addressCheckService = input<{
    checkAddress: (address: RfaAddress) => void;
    addressCheckState: Signal<AsyncState<RfaAddress>>;
  }>();
  protected readonly showStreetAndCityInputs = signal(false);

  protected readonly addressCheckState = computed(() => {
    if (this.addressCheckService()) {
      return this.addressCheckService()?.addressCheckState() as AsyncState<RfaAddress>;
    } else {
      return {
        status: Status.IDLE,
      };
    }
  });

  constructor() {
    effect(
      () => {
        const addressCheck = this.addressCheckState();

        // set form values upon success
        if (addressCheck.status === Status.SUCCESS) {
          this.form().setValue({
            postcode: addressCheck.data?.postcode ?? '',
            houseNumber: addressCheck.data?.houseNumber ?? '',
            houseNumberAddition: addressCheck.data?.houseNumberAddition ?? null,
            street: addressCheck.data?.street ?? '',
            city: addressCheck.data?.city ?? '',
          });
        }

        // change street and city visibility accoring to status
        if (addressCheck.status === Status.SUCCESS) {
          this.showStreetAndCityInputs.set(false);
        }
        if (addressCheck.status === Status.ERROR) {
          this.showStreetAndCityInputs.set(true);
        }
      },
      { allowSignalWrites: true }
    );
  }
  protected readonly postalCodeAutocompleteId = 'postal-code';
  protected readonly houseNumberAutocompleteId = 'housenumber';
  protected readonly componentLabel = 'Adres';
  protected readonly addressCheckError =
    'Deze postcode is onbekend binnen ons systeem, gelieve de straat en plaats zelf in te vullen.';
  protected readonly changeAddressLabel = 'Adres wijzigen';

  private prevAddressString = '';
  private readonly _destroyRef$ = inject(DestroyRef);

  ngOnInit(): void {
    this.form()
      .valueChanges.pipe(
        takeUntilDestroyed(this._destroyRef$),
        debounceTime(600),
        distinctUntilChanged()
      )
      .subscribe((_) => this.handleValueChange());
  }

  get postalCodeControl() {
    return this.form().controls.postcode;
  }

  get houseNumberControl() {
    return this.form().controls.houseNumber;
  }

  get houseNumberAdditionControl() {
    return this.form().controls.houseNumberAddition;
  }

  get cityControl() {
    return this.form().controls.city;
  }

  get streetControl() {
    return this.form().controls.street;
  }

  focusChangeTrackConfig(
    control: RfaFormControl<string, typeof RfaComponents.TextInput>
  ) {
    const focusChangeOptions: RfaFocusChangeTrackerOptions = control.options
      .analytics?.focusChangeTracker ?? {
      fieldName: control.options.label,
    };

    return focusChangeOptionsToConfig(focusChangeOptions, {
      fieldName: control.options.label,
      hashValue: false,
    });
  }

  private handleValueChange(): void {
    if (
      this.form().controls.postcode.invalid ||
      this.form().controls.houseNumber.invalid
    ) {
      return;
    }

    const address = this.form().value as RfaAddress;
    address.houseNumber = address.houseNumber.toString();
    const addressString = this.addressToString(address);

    if (addressString !== this.prevAddressString) {
      if (this.addressCheckService()) {
        this.addressCheckService()?.checkAddress(address);
      }
      this.prevAddressString = addressString;
    }
  }

  private addressToString(address: RfaAddress) {
    return `${address.postcode.toString().trim()}${address.houseNumber.toString().trim()}${address.houseNumberAddition.toString().trim()}`;
  }

  protected ariaDescribedBy(
    control: RfaFormControl<string, typeof RfaComponents._Any>
  ) {
    return getAriaDescribedBy(control);
  }

  protected streetAddress() {
    const address = this.form().value as RfaAddress;
    return `${address.street} ${address.houseNumber}, ${address.city}`;
  }
}
